import { getGeneralOptions } from 'API/generalOptions/generalOptions';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { GEO_LOCATION_ERROR } from 'Enum/geoLocationError';
import { SUPPLIERS_STEPS } from 'Enum/suppliers';
import { useAppSelector, useDictionary, useDispatchApp } from 'Hooks';
import { setGeneralOptions } from 'Store';
import { createContext, useCallback, useEffect, useState } from 'react';

export const SuppliersResultsUrl = 'results';

export interface ICoordinate {
  latitude: number;
  longitude: number;
}

interface ISuppliersContext {
  suppliersId: number;
  setSuppliersId: (index: number) => void;
  stepIndex: number;
  setStepIndex: (index: number) => void;
  userLocation: ICoordinate | null;
  findCity: (cityId: number | undefined) => string;
  initUserLocation: Function;
}

export const SuppliersContext = createContext<ISuppliersContext>({
  suppliersId: 0,
  setSuppliersId: () => {},
  stepIndex: 0,
  setStepIndex: () => {},
  userLocation: null,
  findCity: () => '',
  initUserLocation: () => {},
});

export const SuppliersContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatchApp();

  const [suppliersId, setSuppliersId] = useState<number>(-1);
  const [stepIndex, setStepIndex] = useState<number>(SUPPLIERS_STEPS.LOBBY);
  const [userLocation, setUserLocation] = useState<ICoordinate | null>(null);

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Suppliers
  ).geoLocationErrors;

  const initGeneralOptions = useCallback(async () => {
    const newGeneralOptions = await getGeneralOptions();
    dispatch(setGeneralOptions(newGeneralOptions ?? null));
  }, [dispatch]);

  const initUserLocation = useCallback(async (): Promise<void> => {
    if (navigator.geolocation) {
      return new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject();
        }, 10000);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            clearTimeout(timeout);
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            resolve();
          },
          (error) => {
            clearTimeout(timeout);
            let errorMessage;
            switch (error.code) {
              case GEO_LOCATION_ERROR.PERMISSION_DENIED:
                errorMessage = dictionary.userDeny;
                break;
              case GEO_LOCATION_ERROR.POSITION_UNAVAILABLE:
                errorMessage = dictionary.positionError;
                break;
              case GEO_LOCATION_ERROR.TIMEOUT:
                errorMessage = dictionary.positionError;
                break;
              default:
                errorMessage = dictionary.positionError;
                break;
            }
            alert(errorMessage);
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      });
    }
  }, [dictionary]);

  useEffect(() => {
    initGeneralOptions();
    initUserLocation();
  }, [initGeneralOptions, initUserLocation]);

  const findCity = (cityId: number | undefined) => {
    if (!cityId) return '';
    const city = cities?.find((city: any) => city.id === cityId)?.value;
    return city ? `, ${city}` : '';
  };

  return (
    <SuppliersContext.Provider
      value={{
        suppliersId,
        setSuppliersId,
        stepIndex,
        setStepIndex,
        userLocation,
        findCity,
        initUserLocation,
      }}
    >
      {children}
    </SuppliersContext.Provider>
  );
};
